import { useState } from 'react';
import { ArrowUpRight, Check, Copy, Square, SquareCheck, TriangleAlert } from 'lucide-react';
import { cx } from '../../lib/cx';
import type { SetupStep } from './api';

/**
 * A setup guide you can actually follow.
 *
 * ── WHAT THIS REPLACED, AND WHY ──────────────────────────────────────────
 * Seven numbered paragraphs of 11px prose, all visible at once. Every value
 * that mattered — a URL, a permission, a checkbox that must be OFF — was
 * buried mid-sentence, so following it meant reading a paragraph, finding
 * the value inside it, and retyping it into a form on another tab. The
 * result looked like documentation and worked like a puzzle.
 *
 * Someone doing setup is doing two things: looking at a form, and copying
 * values into it. So: one step at a time, a short heading, a direct link to
 * the exact page, and the values rendered AS a form — label on the left,
 * value on the right, copy button beside it. A checkbox that must be
 * unticked is drawn as an unticked checkbox, because "leave this off" in
 * prose is the single most missed instruction in any setup guide.
 *
 * The steps themselves come from the server (see github.app.ts and
 * github.client.ts) with this deployment's real URLs already substituted, so
 * nothing here is assembled by hand and nothing can drift from the code that
 * consumes it.
 */

function CopyButton({ value }: { readonly value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1_600);
        });
      }}
      title="Copy"
      className="flex-none rounded-md border border-line-strong p-1 text-muted hover:border-line-heavy hover:text-ink"
    >
      {copied ? (
        <Check size={12} strokeWidth={2.5} className="text-success" aria-hidden />
      ) : (
        <Copy size={12} strokeWidth={1.75} aria-hidden />
      )}
      <span className="sr-only">{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );
}

/** One row, drawn to look like the field it is telling you to fill in. */
function FieldRow({ field }: { readonly field: NonNullable<SetupStep['fields']>[number] }) {
  const toggle = field.set !== undefined;

  return (
    <div className="flex items-center gap-2 border-t border-line py-1.5 first:border-t-0">
      <span className="w-[10rem] flex-none fs-11 text-muted">{field.label}</span>
      {toggle ? (
        <span
          className={cx(
            'inline-flex items-center gap-1.5 fs-12 font-semibold',
            field.set === true ? 'text-success' : 'text-warn',
          )}
        >
          {field.set === true ? (
            <SquareCheck size={14} strokeWidth={2} aria-hidden />
          ) : (
            <Square size={14} strokeWidth={2} aria-hidden />
          )}
          {field.value}
        </span>
      ) : (
        <>
          <code className="min-w-0 flex-1 truncate rounded-md bg-surface-2 px-2 py-1 font-mono fs-11 text-ink">
            {field.value}
          </code>
          {field.copy === true && <CopyButton value={field.value} />}
        </>
      )}
    </div>
  );
}

export function SetupSteps({ steps }: { readonly steps: readonly SetupStep[] }) {
  const [at, setAt] = useState(0);
  const step = steps[at];
  if (step === undefined) return null;

  const last = at === steps.length - 1;

  return (
    <div className="rounded-[10px] border border-line bg-base p-3.5">
      {/* Progress as dots rather than a bar: six steps is a number you can
          see at a glance, and a clickable dot lets someone go back to the one
          they are actually on rather than restarting. */}
      <div className="mb-3 flex items-center gap-1.5">
        {steps.map((item, index) => (
          <button
            key={item.title}
            type="button"
            onClick={() => setAt(index)}
            title={item.title}
            aria-label={`Step ${index + 1}: ${item.title}`}
            aria-current={index === at}
            className={cx(
              'h-1.5 flex-1 rounded-full transition-colors',
              index === at ? 'bg-accent' : index < at ? 'bg-success' : 'bg-line-heavy',
            )}
          />
        ))}
        <span className="ml-1.5 flex-none fs-11 tabular-nums text-faint">
          {at + 1}/{steps.length}
        </span>
      </div>

      <h4 className="fs-13 font-semibold text-ink">{step.title}</h4>
      {step.why !== undefined && <p className="mt-0.5 fs-12 text-muted">{step.why}</p>}

      {step.open !== undefined && (
        <a
          href={step.open.url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-line-strong px-2.5 py-1.5 fs-12 font-semibold text-accent hover:border-line-heavy"
        >
          {step.open.label}
          <ArrowUpRight size={13} strokeWidth={2} aria-hidden />
        </a>
      )}

      {step.fields !== undefined && step.fields.length > 0 && (
        <div className="mt-2.5 rounded-lg border border-line px-2.5 py-1">
          {step.fields.map((field) => (
            <FieldRow key={field.label} field={field} />
          ))}
        </div>
      )}

      {step.warn !== undefined && (
        <p className="mt-2.5 flex items-start gap-1.5 fs-11 text-warn">
          <TriangleAlert size={12} strokeWidth={2} className="mt-0.5 flex-none" aria-hidden />
          {step.warn}
        </p>
      )}

      <div className="mt-3 flex items-center gap-2 border-t border-line pt-2.5">
        <button
          type="button"
          disabled={at === 0}
          onClick={() => setAt(at - 1)}
          className="rounded-lg px-2.5 py-1 fs-11 font-semibold text-muted hover:text-ink disabled:opacity-40"
        >
          Back
        </button>
        <button
          type="button"
          disabled={last}
          onClick={() => setAt(at + 1)}
          className="ml-auto rounded-lg bg-ink px-3 py-1.5 fs-11 font-semibold text-white disabled:opacity-40"
        >
          {last ? 'Done' : 'Next'}
        </button>
      </div>
    </div>
  );
}
